import { useEffect, useState } from "react";
import {
  FaBan,
  FaCheck,
  FaCheckCircle,
  FaExclamationCircle,
  FaTrash,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import FilterPanel from "../../components/FilterPanel";
import Input from "../../components/Input";
import PageHeader from "../../components/PageHeader";
import Pagination from "../../components/Pagination";
import Select from "../../components/Select";
import Table from "../../components/Table";
import { api } from "../../services/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: "",
    verified: "",
    search: "",
  });
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });

  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [filters, pagination.page]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: "20",
      });
      if (filters.role) params.append("role", filters.role);
      if (filters.verified) params.append("verified", filters.verified);
      if (filters.search) params.append("search", filters.search);

      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.users || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination.total,
        pages: response.pagination.pages,
      }));

      // Clear selection when loading new data
      setSelectedUsers(new Set());
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map((u) => u._id)));
    }
  };

  const toggleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Individual actions
  const handleBan = async (userId, ban) => {
    try {
      await api.put(`/admin/users/${userId}/${ban ? "ban" : "unban"}`);
      toast.success(`User ${ban ? "banned" : "unbanned"} successfully`);
      loadUsers();
    } catch (error) {
      toast.error(error.error || "Operation failed");
    }
  };

  const handleVerify = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/verify`);
      toast.success("User verified successfully");
      loadUsers();
    } catch (error) {
      toast.error(error.error || "Operation failed");
    }
  };

  const handleDelete = async (userId) => {
    const result = await Swal.fire({
      title: "Delete User",
      text: "Are you sure you want to delete this user? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/users/${userId}`);
        toast.success("User deleted successfully");
        loadUsers();
      } catch (error) {
        toast.error(error.error || "Delete failed");
      }
    }
  };

  // Bulk actions
  const handleBulkAction = async (action, title, message) => {
    if (selectedUsers.size === 0) {
      toast.warning("Please select at least one user");
      return;
    }
    const result = await Swal.fire({
      title,
      text: `${message} (${selectedUsers.size} user${
        selectedUsers.size > 1 ? "s" : ""
      })`,
      icon: action === "delete" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: title.replace("Bulk ", ""),
      confirmButtonColor: action === "delete" ? "#d33" : undefined,
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      setBulkActionLoading(true);
      try {
        const userIds = Array.from(selectedUsers);
        const response = await api.post(`/admin/users/bulk-${action}`, {
          userIds,
        });
        toast.success(response.message || "Bulk action completed");
        loadUsers();
      } catch (error) {
        toast.error(error.error || "Bulk action failed");
      } finally {
        setBulkActionLoading(false);
      }
    }
  };

  const roleOptions = [
    { value: "", label: "All Roles" },
    { value: "student", label: "Student" },
    { value: "tutor", label: "Tutor" },
    { value: "admin", label: "Admin" },
  ];

  const verifiedOptions = [
    { value: "", label: "All" },
    { value: "true", label: "Verified" },
    { value: "false", label: "Unverified" },
  ];

  const columns = [
    {
      header: (
        <input
          type="checkbox"
          checked={selectedUsers.size === users.length && users.length > 0}
          onChange={toggleSelectAll}
          className="rounded border-border text-primary focus:ring-primary"
        />
      ),
      render: (user) => (
        <input
          type="checkbox"
          checked={selectedUsers.has(user._id)}
          onChange={() => toggleSelectUser(user._id)}
          className="rounded border-border text-primary focus:ring-primary"
        />
      ),
      sortable: false,
    },
    {
      header: "Name",
      accessor: "name.first",
      sortable: true,
      render: (user) => `${user.name?.first} ${user.name?.last}`,
    },
    {
      header: "Email",
      accessor: "email",
      sortable: true,
      render: (user) => <span className="opacity-80">{user.email}</span>,
    },
    {
      header: "Role",
      accessor: "role",
      sortable: true,
      render: (user) => (
        <Badge
          label={user.role}
          variant={
            user.role === "admin"
              ? "warning"
              : user.role === "tutor"
              ? "primary"
              : "secondary"
          }
          size="small"
          className="font-medium capitalize"
        />
      ),
    },
    {
      header: "Status",
      accessor: "isVerified",
      sortable: true,
      render: (user) => (
        <div className="flex flex-col gap-1">
          {user.isVerified ? (
            <span className="text-xs text-secondary inline-flex items-center gap-1">
              <FaCheckCircle /> Verified
            </span>
          ) : (
            <span className="text-xs text-accent inline-flex items-center gap-1">
              <FaExclamationCircle /> Unverified
            </span>
          )}
          {user.metadata?.banned && (
            <span className="text-xs text-accent inline-flex items-center gap-1">
              <FaBan /> Banned
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Actions",
      sortable: false,
      render: (user) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm">
            <Link
              to={`/admin/users/${user._id}`}
              className="text-primary hover:opacity-80"
            >
              View
            </Link>
          </Button>
          {!user.isVerified && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleVerify(user._id)}
              className="flex items-center gap-2"
            >
              <FaCheck className="w-3 h-3" />
              Verify
            </Button>
          )}
          {user.metadata?.banned ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleBan(user._id, false)}
              className="flex items-center gap-2"
            >
              <FaBan className="w-3 h-3" />
              Unban
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleBan(user._id, true)}
              className="flex items-center gap-2"
            >
              <FaBan className="w-3 h-3" />
              Ban
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(user._id)}
            className="flex items-center gap-2"
          >
            <FaTrash className="w-3 h-3" />
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="User Management"
        action={
          <Link to="/admin/users/new">
            <Button>Create User</Button>
          </Link>
        }
      />

      <FilterPanel>
        <Select
          label="Role"
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          options={roleOptions}
        />
        <Select
          label="Verified"
          value={filters.verified}
          onChange={(e) => setFilters({ ...filters, verified: e.target.value })}
          options={verifiedOptions}
        />
        <div className="md:col-span-2">
          <Input
            label="Search"
            type="text"
            placeholder="Search by name or email..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
      </FilterPanel>

      {selectedUsers.size > 0 && (
        <div className="bg-primary/10 border border-primary/40 rounded-lg p-4 mb-4 flex items-center justify-between">
          <span className="text-sm font-medium text-primary">
            {selectedUsers.size} user{selectedUsers.size > 1 ? "s" : ""}{" "}
            selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                handleBulkAction(
                  "verify",
                  "Bulk Verify",
                  "Verify selected users?"
                )
              }
              disabled={bulkActionLoading}
            >
              Verify
            </Button>
            <Button
              size="sm"
              onClick={() =>
                handleBulkAction("ban", "Bulk Ban", "Ban selected users?")
              }
              disabled={bulkActionLoading}
            >
              Ban
            </Button>
            <Button
              size="sm"
              onClick={() =>
                handleBulkAction("unban", "Bulk Unban", "Unban selected users?")
              }
              disabled={bulkActionLoading}
            >
              Unban
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() =>
                handleBulkAction(
                  "delete",
                  "Bulk Delete",
                  "Delete selected users? This action cannot be undone."
                )
              }
              disabled={bulkActionLoading}
            >
              Delete
            </Button>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={users.map((u) => ({ ...u, id: u._id }))}
        loading={loading}
        emptyMessage="No users found"
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
      />
    </div>
  );
}
